import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveal – fades and slides its children up when they scroll into view.
 * Uses IntersectionObserver, runs once, supports a per-item delay for stagger effects.
 *
 * Props:
 *   delay:    ms delay before revealing (default 0)
 *   as:       tag or component to render as (default 'div')
 *   distance: pixels of upward travel (default 24)
 *   className / style: passed through
 */
const Reveal = ({
  children,
  delay = 0,
  as: Tag = 'div',
  distance = 24,
  className = '',
  style = {},
  ...rest
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const combinedStyle = {
    ...style,
    transitionDelay: `${delay}ms`,
    transform: visible ? 'translateY(0)' : `translateY(${distance}px)`,
    opacity: visible ? 1 : 0,
    transitionProperty: 'opacity, transform',
    transitionDuration: '700ms',
    transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
    willChange: 'opacity, transform',
  };

  return (
    <Tag ref={ref} className={className} style={combinedStyle} {...rest}>
      {children}
    </Tag>
  );
};

export default Reveal;
